import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ScanResults } from '@/types/health.types';
import type { WhatsAppDeliveryStatus } from '@/services/ScanApiService';

/** Backend save of the scan, which also issues the report link and WhatsApp send. */
export type ReportDelivery =
  | { status: 'saving' }
  | {
      status: 'saved';
      reportToken: string;
      reportUrl: string;
      whatsapp: WhatsAppDeliveryStatus;
    }
  | { status: 'failed' };

export interface ResultsContextValue {
  results: ScanResults | null;
  report: ReportDelivery | null;
  setResults: (data: ScanResults) => void;
  setReport: (report: ReportDelivery) => void;
  clearResults: () => void;
}

const ResultsContext = createContext<ResultsContextValue | null>(null);

export function ResultsProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [results, setResultsState] = useState<ScanResults | null>(null);
  const [report, setReport] = useState<ReportDelivery | null>(null);

  const setResults = useCallback((data: ScanResults) => {
    setResultsState(data);
    setReport(null);
  }, []);

  const clearResults = useCallback(() => {
    setResultsState(null);
    setReport(null);
  }, []);

  const value = useMemo(
    () => ({ results, report, setResults, setReport, clearResults }),
    [results, report, setResults, clearResults],
  );

  return <ResultsContext.Provider value={value}>{children}</ResultsContext.Provider>;
}

export function useResults(): ResultsContextValue {
  const ctx = useContext(ResultsContext);
  if (!ctx) throw new Error('useResults must be used within ResultsProvider');
  return ctx;
}
